import { useCallback, useRef, useState } from "react";
/**
 * Total record count of a paged search list. Since DB-876 the backend counts
 * `/titles/search` and `/products/search` exactly on the first page and answers
 * later pages with that remembered count (60 s) or, on the short last page,
 * `offset + rows`. A page without a count keeps the total remembered from the
 * first page of the same search.
 *
 * @param knownTotal total remembered from the first page of the same search, if any
 * @param offset offset of the page just loaded
 * @param responseTotal `totalRecords` of that page
 * @param itemCount number of records on that page
 * @returns the total the pager should show
 */
export var resolveListTotal = function (knownTotal, offset, responseTotal, itemCount) {
    if (typeof responseTotal === "number")
        return responseTotal;
    if (offset > 0 && knownTotal !== undefined)
        return knownTotal;
    return offset + itemCount;
};
/**
 * Keeps the pager total of a list stable while paging: the count of the first
 * page is remembered per search (`searchKey` — everything but offset, page
 * size and sort) and reused for a later page that comes without a count.
 *
 * @param searchKey identity of the current search
 */
export var useListTotal = function (searchKey) {
    var knownTotalsRef = useRef(new Map());
    var _a = useState(0), totalRecords = _a[0], setTotalRecords = _a[1];
    var recordPage = useCallback(function (offset, responseTotal, itemCount) {
        var known = knownTotalsRef.current.get(searchKey);
        var total = resolveListTotal(known, offset, responseTotal, itemCount);
        if (offset === 0 || known === undefined) {
            knownTotalsRef.current.set(searchKey, total);
        }
        setTotalRecords(total);
    }, [searchKey]);
    return { totalRecords: totalRecords, recordPage: recordPage };
};
